import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import AnimatedSection from '../AnimatedSection';
import { useAuth } from '../../context/AuthContext';
import {
  PARTNER_CATEGORY_LABELS,
  PARTNER_STATUS_LABELS,
  PARTNER_STATUS_TONES,
  PARTNER_TIER_LABELS,
  PartnerStatus,
} from '../../constants/partnerNetwork';
import {
  AdminPartner,
  listPartners,
  updatePartnerStatus,
} from '../../services/partnerNetwork';
import { formatDate, formatMoney, formatRate } from '../../utils/partnerFormat';
import {
  Alert,
  EmptyState,
  Modal,
  Pagination,
  Spinner,
  StatusBadge,
  filterClass,
  inputClass,
} from './ui';

const STATUSES: PartnerStatus[] = ['pending', 'active', 'suspended', 'rejected'];

/** Admin view of every partner, and the queue of registrations awaiting a decision. */
const PartnerList: React.FC = () => {
  const { admin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [partners, setPartners] = useState<AdminPartner[]>([]);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 25, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [decision, setDecision] = useState<{
    partner: AdminPartner;
    status: PartnerStatus;
  } | null>(null);
  const [reason, setReason] = useState('');

  const status = searchParams.get('status') || '';
  const page = Number(searchParams.get('page') || 1);
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const search = searchParams.get('search') || '';

  const canManage = admin?.role === 'admin' || admin?.role === 'superadmin';

  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (searchInput === search) return;
      setSearchParams((current) => {
        const next = new URLSearchParams(current);
        if (searchInput) next.set('search', searchInput);
        else next.delete('search');
        next.delete('page');
        return next;
      });
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchInput, search, setSearchParams]);

  const setFilter = (key: string, value: string | null) => {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      if (value) next.set(key, value);
      else next.delete(key);
      if (key !== 'page') next.delete('page');
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await listPartners({
        search: search || undefined,
        status: status || undefined,
        page,
        limit: 25,
      });
      setPartners(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load partners');
    } finally {
      setLoading(false);
    }
  }, [search, status, page]);

  useEffect(() => {
    load();
  }, [load]);

  const applyDecision = async () => {
    if (!decision) return;

    setError(null);
    try {
      setBusy(true);
      const updated = await updatePartnerStatus(
        decision.partner.id,
        decision.status,
        reason.trim() || undefined
      );

      setNotice(
        decision.status === 'active'
          ? `${updated.fullName} approved as ${updated.partnerId}. Their welcome email has gone out.`
          : `${updated.fullName} marked ${PARTNER_STATUS_LABELS[decision.status].toLowerCase()}.`
      );
      setDecision(null);
      setReason('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update the partner');
    } finally {
      setBusy(false);
    }
  };

  const pendingCount = partners.filter((p) => p.status === 'pending').length;

  return (
    <section className="py-8 md:py-12">
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-8">
        <AnimatedSection animation="slide-up">
          <div className="mb-8">
            <h1 className="mb-2 font-display text-2xl font-semibold text-white md:text-3xl">
              Active partners
            </h1>
            <p className="text-sm text-muted">
              Everyone with a partner account, and anyone waiting on approval.
            </p>
          </div>
        </AnimatedSection>

        {error && (
          <Alert tone="error" className="mb-6">
            {error}
          </Alert>
        )}
        {notice && !error && (
          <Alert tone="success" className="mb-6">
            {notice}
          </Alert>
        )}

        {pendingCount > 0 && status !== 'pending' && (
          <Alert tone="warning" className="mb-6">
            {pendingCount} {pendingCount === 1 ? 'registration is' : 'registrations are'}{' '}
            waiting for approval on this page.{' '}
            <button
              type="button"
              onClick={() => setFilter('status', 'pending')}
              className="underline underline-offset-2"
            >
              Show only pending
            </button>
          </Alert>
        )}

        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-xl border border-surface-border bg-surface-card p-4">
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search name, company, email or partner ID…"
            className={`${filterClass} min-w-[220px] flex-1`}
          />
          <select
            value={status}
            onChange={(e) => setFilter('status', e.target.value || null)}
            className={filterClass}
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {PARTNER_STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        <div className="rounded-xl border border-surface-border bg-surface-card">
          {loading ? (
            <div className="flex justify-center py-20">
              <Spinner className="h-10 w-10" />
            </div>
          ) : partners.length === 0 ? (
            <div className="p-4">
              <EmptyState
                title="No partners yet"
                description="Partners appear here once a prospect is converted, or someone registers through the partner portal and is approved."
              />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[1100px] text-sm">
                <thead>
                  <tr className="border-b border-surface-border text-left text-xs uppercase tracking-wide text-muted">
                    <th className="px-4 py-3 font-medium">Partner ID</th>
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Company</th>
                    <th className="px-4 py-3 font-medium">Category</th>
                    <th className="px-4 py-3 font-medium">Status</th>
                    <th className="px-4 py-3 font-medium">Rate</th>
                    <th className="px-4 py-3 font-medium">Referrals</th>
                    <th className="px-4 py-3 font-medium">Earned</th>
                    <th className="px-4 py-3 font-medium">Joined</th>
                    <th className="px-4 py-3 text-right font-medium">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {partners.map((partner) => (
                    <tr
                      key={partner.id}
                      className="border-b border-surface-border/60 transition-colors last:border-0 hover:bg-surface-hover/40"
                    >
                      <td className="px-4 py-3 font-mono text-xs text-muted">
                        {partner.partnerId.startsWith('PENDING-') ? '—' : partner.partnerId}
                      </td>
                      <td className="px-4 py-3">
                        <Link
                          to={`/admin/partners/partners/${partner.id}`}
                          className="font-medium text-white hover:text-primary-400"
                        >
                          {partner.fullName}
                        </Link>
                        <span className="block max-w-[200px] truncate text-xs text-muted">
                          {partner.email}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted">{partner.companyName || '—'}</td>
                      <td className="px-4 py-3 text-muted">
                        {partner.category ? PARTNER_CATEGORY_LABELS[partner.category] : '—'}
                        {partner.tier && (
                          <span className="block text-xs text-muted/70">
                            {PARTNER_TIER_LABELS[partner.tier]}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <StatusBadge
                          label={PARTNER_STATUS_LABELS[partner.status]}
                          tone={PARTNER_STATUS_TONES[partner.status]}
                        />
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {formatRate(partner.commissionRate)}
                      </td>
                      <td className="px-4 py-3 text-muted">
                        {partner.referralCount ?? 0}
                        <span className="block text-xs text-muted/70">
                          {partner.wonReferrals ?? 0} won
                        </span>
                      </td>
                      <td className="px-4 py-3 text-white">
                        {formatMoney(partner.totalEarned ?? 0)}
                        <span className="block text-xs text-muted">
                          {formatMoney(partner.totalPaid ?? 0)} paid
                        </span>
                      </td>
                      <td className="px-4 py-3 text-muted">{formatDate(partner.createdAt)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap items-center justify-end gap-2">
                          {canManage && partner.status === 'pending' && (
                            <>
                              <button
                                type="button"
                                onClick={() => setDecision({ partner, status: 'active' })}
                                className="rounded-lg border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs text-primary-400 transition-colors hover:bg-primary-500/20"
                              >
                                Approve
                              </button>
                              <button
                                type="button"
                                onClick={() => setDecision({ partner, status: 'rejected' })}
                                className="rounded-lg border border-surface-border px-3 py-1.5 text-xs text-muted transition-colors hover:text-white"
                              >
                                Decline
                              </button>
                            </>
                          )}
                          {canManage && partner.status === 'active' && (
                            <button
                              type="button"
                              onClick={() => setDecision({ partner, status: 'suspended' })}
                              className="rounded-lg border border-surface-border px-3 py-1.5 text-xs text-muted transition-colors hover:text-white"
                            >
                              Suspend
                            </button>
                          )}
                          {canManage && partner.status === 'suspended' && (
                            <button
                              type="button"
                              onClick={() => setDecision({ partner, status: 'active' })}
                              className="rounded-lg border border-primary-500/40 bg-primary-500/10 px-3 py-1.5 text-xs text-primary-400 transition-colors hover:bg-primary-500/20"
                            >
                              Reinstate
                            </button>
                          )}
                          <Link
                            to={`/admin/partners/partners/${partner.id}`}
                            className="rounded-lg px-3 py-1.5 text-xs text-muted transition-colors hover:text-white"
                          >
                            View
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loading && partners.length > 0 && (
            <div className="border-t border-surface-border px-4">
              <Pagination
                page={pagination.page}
                pages={pagination.pages}
                total={pagination.total}
                onChange={(next) => setFilter('page', String(next))}
              />
            </div>
          )}
        </div>
      </div>

      <Modal
        open={Boolean(decision)}
        title={
          decision?.status === 'active'
            ? 'Approve partner'
            : decision?.status === 'rejected'
            ? 'Decline application'
            : 'Suspend partner'
        }
        onClose={() => {
          setDecision(null);
          setReason('');
        }}
        footer={
          <>
            <button
              type="button"
              onClick={() => {
                setDecision(null);
                setReason('');
              }}
              className="btn-secondary py-2.5 px-5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyDecision}
              disabled={busy}
              className="btn-primary py-2.5 px-5 disabled:opacity-60"
            >
              {busy ? 'Saving…' : 'Confirm'}
            </button>
          </>
        }
      >
        {decision && (
          <div className="space-y-4 text-sm">
            {decision.status === 'active' ? (
              <p className="text-muted">
                <span className="text-white">{decision.partner.fullName}</span> gets a Partner
                ID, portal access, and a welcome email with their sign-in link.
              </p>
            ) : decision.status === 'rejected' ? (
              <p className="text-muted">
                <span className="text-white">{decision.partner.fullName}</span> is told their
                application was not taken forward. The record is kept.
              </p>
            ) : (
              <p className="text-muted">
                <span className="text-white">{decision.partner.fullName}</span> loses portal
                access immediately. Their referrals and commissions are untouched.
              </p>
            )}

            {decision.status !== 'active' && (
              <div>
                <label className="mb-2 block text-muted">Reason (optional)</label>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="Included in the email where one is sent."
                  className={`${inputClass} resize-y`}
                />
              </div>
            )}
          </div>
        )}
      </Modal>
    </section>
  );
};

export default PartnerList;
